import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);
  return matches;
};

const COLOR_PRIMARY = "#0E5D9D";

const formatRangeForDisplay = (rangeString, forXAxis = false) => {
  if (!rangeString) return "";

  // Case: "dd/mm/yyyy hh:mm-hh:mm"
  const dateTimeRangeMatch = rangeString.match(
    /^(\d{2}\/\d{2}\/\d{4}) (\d{2}:\d{2})-(\d{2}:\d{2})$/
  );

  if (dateTimeRangeMatch) {
    const [, date, startTime, endTime] = dateTimeRangeMatch;

    if (startTime === endTime) {
      // Same time → just show date
      return date;
    }

    if (forXAxis) {
      // ✅ Show date and time range in brackets on next line
      return `${date}\n[${startTime}-${endTime}]`;
    }

    // Tooltip / legend → single line
    return `${date} [${startTime}-${endTime}]`;
  }

  // Case: ranges like "12/09/2025 to 13/09/2025" or with " - "
  if (rangeString.includes(" to ") || rangeString.includes(" - ")) {
    const separator = rangeString.includes(" to ") ? " to " : " - ";
    const [start, end] = rangeString.split(separator).map((s) => s.trim());

    const startDate = start.split(" ")[0];
    const endDate = end.split(" ")[0];

    if (startDate === endDate) {
      return startDate; // Same date
    }

    return forXAxis
      ? `${startDate}\nto\n${endDate}`
      : `${startDate} to ${endDate}`;
  }

  // Fallback: strip any time part
  return rangeString.split(" ")[0];
};


const CustomBarChartTick = ({ x, y, payload }) => {
  if (!payload || !payload.value) return null;
  const lines = String(payload.value).split("\n");
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          dy={12 + i * 16}
          textAnchor="middle"
          fill="#6c757d"
          fontSize="12"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

export const CustomEngagementRateChart = ({
  engagementData = [],
  xLabels = [],
  xAxisTitle = "Time Intervals",
  tooltipLabels = [],
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const chartContainerRef = useRef(null);
  const [tooltipData, setTooltipData] = useState(null); // { x, y, payload }

  // State for the legend tooltip on mobile
  const [legendTooltip, setLegendTooltip] = useState(null);
  const legendTooltipRef = useRef(null); // Ref for the legend's custom tooltip

  const chartRechartsData = useMemo(
    () =>
      engagementData.map((rate, index) => {
        const rawLabel = tooltipLabels[index] || xLabels[index];
        return {
          // Use formatRangeForDisplay with forXAxis = true for X-axis labels
          name: isMobile ? `${index + 1}` : formatRangeForDisplay(rawLabel, true),
          value: rate,
          index: index + 1,
          originalLabel: rawLabel,
        };
      }),
    [engagementData, xLabels, tooltipLabels, isMobile]
  );

  const yAxisDomain = useMemo(() => {
    let maxRate = Math.max(...chartRechartsData.map((d) => d.value || 0), 0);
    if (maxRate === 0) return [0, 10];
    let maxTickValue = Math.min(100, Math.ceil(maxRate / 5) * 5);
    return [0, maxTickValue];
  }, [chartRechartsData]);

  const chartMargin = useMemo(
    () =>
      isMobile
        ? { top: 15, right: 15, left: -15, bottom: 20 }
        : { top: 15, right: 30, left: 10, bottom: 20 },
    [isMobile]
  );

  const handleBarMouseEnter = useCallback((data, index, event) => {
    if (!data.value || data.value <= 0 || !chartContainerRef.current) return;

    const containerRect = chartContainerRef.current.getBoundingClientRect();
    const x = event.clientX - containerRect.left;
    const y = event.clientY - containerRect.top;

    setTooltipData({
      active: true,
      payload: [
        {
          value: data.value,
          payload: { originalLabel: data.originalLabel },
        },
      ],
      coordinate: { x, y },
    });
  }, []);

  const handleBarMouseLeave = useCallback(() => {
    setTooltipData(null);
  }, []);

  const renderCustomTooltip = () => {
    if (!tooltipData || !tooltipData.active || !tooltipData.payload) return null;

    const { payload, coordinate } = tooltipData;
    const { value, payload: originalPayload } = payload[0];
    // Tooltip should always be single line
    const displayLabel = formatRangeForDisplay(originalPayload.originalLabel, false);

    return (
      <div
        className="bg-black text-white text-xs px-3 py-2 rounded shadow-md"
        style={{
          position: "absolute",
          left: coordinate.x,
          top: coordinate.y - 35,
          transform: "translateX(-50%)",
          pointerEvents: "none",
          zIndex: 1000,
          minWidth: 140,
        }}
      >
        <div style={{ fontWeight: 500, marginBottom: "4px", whiteSpace: "nowrap" }}>
          {displayLabel}
        </div>
        <div>
          <span style={{ color: COLOR_PRIMARY }}></span> Engagement: {Number(value).toFixed(2)}%
        </div>
      </div>
    );
  };

  const updateLegendTooltipPosition = useCallback((event, text, value) => {
    if (!value || value <= 0) return;
    const displayTooltipText = formatRangeForDisplay(text, false);
    setLegendTooltip({ text: displayTooltipText, rect: event.currentTarget.getBoundingClientRect() });
  }, []);

  useEffect(() => {
    if (legendTooltip && legendTooltipRef.current) {
      const tooltipEl = legendTooltipRef.current;
      const rect = legendTooltip.rect;
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      let x = rect.left + rect.width / 2;
      let y = rect.top - 10;

      if (x - tooltipRect.width / 2 < 0) x = tooltipRect.width / 2 + 5;
      else if (x + tooltipRect.width / 2 > viewportWidth) x = viewportWidth - tooltipRect.width / 2 - 5;

      if (y - tooltipRect.height < 0) {
        tooltipEl.style.transform = `translate(-50%, 0%)`;
        y = rect.bottom + 10;
      } else {
        tooltipEl.style.transform = `translate(-50%, -100%)`;
      }

      tooltipEl.style.left = `${x}px`;
      tooltipEl.style.top = `${y}px`;
    }
  }, [legendTooltip]);

  let touchTimeout = useRef(null);
  const handleTouchStart = useCallback(
    (index, date, event, value) => {
      if (!isMobile || !value || value <= 0) return;
      if (touchTimeout.current) clearTimeout(touchTimeout.current);
      touchTimeout.current = setTimeout(() => updateLegendTooltipPosition(event, date, value), 500);
    },
    [isMobile, updateLegendTooltipPosition]
  );

  const handleTouchMove = useCallback(() => {
    if (!isMobile || !legendTooltip) return;
    if (touchTimeout.current) clearTimeout(touchTimeout.current);
    setLegendTooltip(null);
  }, [isMobile, legendTooltip]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    if (touchTimeout.current) clearTimeout(touchTimeout.current);
    setLegendTooltip(null);
  }, [isMobile]);

  const handleMouseEnter = useCallback(
    (index, date, event, value) => {
      if (!isMobile || !value || value <= 0) return;
      updateLegendTooltipPosition(event, date, value);
    },
    [isMobile, updateLegendTooltipPosition]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) return;
    setLegendTooltip(null);
  }, [isMobile]);


  if (!chartRechartsData.length)
    return (
      <div className="flex items-center justify-center w-full h-full text-gray-400 font-archivo">
        No engagement data available.
      </div>
    );


  return (
    <div className="w-full h-full flex flex-col min-h-0 font-archivo relative">
      <div ref={chartContainerRef} className="w-full flex-1 min-h-0 relative">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartRechartsData}
            margin={chartMargin}
            barCategoryGap={isMobile ? "30%" : "43%"}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={!isMobile ? <CustomBarChartTick /> : undefined}
              interval={0}
              height={isMobile ? 40 : 70}
              label={{
                value: isMobile ? "Intervals" : xAxisTitle,
                position: "insideBottom",
                offset: isMobile ? -5 : -10,
                style: { fill: "black", fontSize: isMobile ? 11 : 14 },
              }}
              padding={{ left: 20, right: 20 }}
            />
            <YAxis
              domain={yAxisDomain}
              tick={{ fill: "#6c757d", fontSize: isMobile ? 10 : 13 }}
              tickCount={5}
              interval={0}
              allowDataOverflow
              tickFormatter={(tick) => `${tick}%`}
              label={{
                value: "Avg. Engagement Rate (%)",
                angle: -90,
                position: "insideLeft",
                offset: isMobile ? -5 : 0,
                style: { textAnchor: "middle", fill: "black", fontSize: isMobile ? 11 : 14 },
              }}
            />
            <Bar
              dataKey="value"
              fill={COLOR_PRIMARY}
              radius={[10, 10, 10, 10]}
              isAnimationActive={!tooltipData}
              animationDuration={1000}
              animationEasing="ease-out"
              animationBegin={0}
              onMouseEnter={handleBarMouseEnter}
              onMouseLeave={handleBarMouseLeave}
            />
          </BarChart>
        </ResponsiveContainer>
        {renderCustomTooltip()}
      </div>

      {/* Mobile Date Legend */}
      {isMobile && (
        <div className="mt-2 px-2 relative">
          <h4 className="text-sm font-semibold text-gray-600 mb-2">Data Legend</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-500">
            {chartRechartsData.map((point) => {
              if (!point.value || point.value <= 0) return null; // Skip zero-value entries
              // For the mobile legend display, it should be single line
              const displayLegendLabel = formatRangeForDisplay(point.originalLabel, false);
              return (
                <div
                  key={point.index}
                  className="flex items-center truncate cursor-pointer"
                  onTouchStart={(e) => handleTouchStart(point.index, point.originalLabel, e, point.value)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseEnter={(e) => handleMouseEnter(point.index, point.originalLabel, e, point.value)}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="font-semibold w-6 flex-shrink-0">{point.index}:</span>
                  <span className="truncate">{displayLegendLabel.replace('\n', ' ')}</span>
                </div>
              );
            })}
          </div>
          {legendTooltip && (
            <div
              ref={legendTooltipRef}
              className="fixed bg-black text-white text-xs px-2 py-1 rounded shadow-md"
              style={{ zIndex: 10000, whiteSpace: "pre-line", pointerEvents: "none" }}
            >
              {legendTooltip.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomEngagementRateChart);